import React, { Component, ReactNode } from 'react';
import { AuthStateContext } from './AuthServiceProvider';
import { withAuthState } from './hocs';

const DEFAULT_BUFFER_S = 5 /* minutes */ * 60;

export interface Props {
  authState: AuthStateContext;
  bufferSeconds?: number;
  children?: ReactNode;
}

interface State {
  checkHandle?: number;
  showChildren: boolean;
}

/**
 * A non-visual component that will show or hide its children based on whether the user's session
 * is about to expire. The buffer before the expiration in which the children will be shown is
 * configurable via the bufferSeconds prop.
 *
 * This handles not only whether to show the children on a render cycle, but also has logic to
 * kick off a render cycle to show children if the component was initially rendered before the
 * buffer. Without this extra logic, a re-render never happens and children are permanently
 * hidden.
 *
 * This only applies to internal sessions. External sessions are based on an idle timeout. In an
 * externally facing app, this will never show its children.
 */
class AuthExpiration extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showChildren: false,
    };
  }

  private cancelScheduledCheck() {
    const { checkHandle } = this.state;
    if (!checkHandle) {
      return;
    }
    clearTimeout(checkHandle);
  }

  /**
   * Determines whether the children of this component should be shown or not based on whether the
   * expiration of the session is within the configured buffer or not.
   *
   * If the children should not be shown (meaning the session is not about to expire), a timer is
   * set to check again the next time the component thinks the children should be shown. This works
   * around an issue where once the component is rendered, it will likely never be rendered again
   * because the only state that's being changed is time.
   */
  private determineShowState() {
    this.cancelScheduledCheck();
    if (this.withinExpirationBuffer()) {
      this.setState({ showChildren: true });
      // don't need to schedule anything else, as the children are being shown, if the expiration
      // changes, that will call this method again
      return;
    }
    this.setState({ showChildren: false });
    // without something to force a re-render of the component, it will never show automatically,
    // this schedules a timeout that "wakes up" the component by checking if its state should change
    // about when the buffer starts
    this.scheduleCheck();
  }

  private expirationBufferStart() {
    const { authState, bufferSeconds = DEFAULT_BUFFER_S } = this.props;

    if (!authState?.authenticated) {
      throw new Error('Cannot calculate expiration buffer start when not authenticated.');
    }

    if (!authState?.expiresAt) {
      throw new Error('Cannot calculate expiration buffer start without expiresAt.');
    }
    const bufferMs = bufferSeconds * 1000;
    return authState.expiresAt - bufferMs;
  }

  private scheduleCheck() {
    const { authState } = this.props;
    if (!authState?.authenticated || !authState?.expiresAt) {
      // avoid scheduling anything when there is no way to calculate the buffer start
      return;
    }
    // a millisecond is added just so the time is firmly within the buffer
    const msFromNow = this.expirationBufferStart() - Date.now() + 1;
    const checkHandle = window.setTimeout(() => this.determineShowState(), msFromNow);
    this.setState({ checkHandle });
  }

  private withinExpirationBuffer() {
    const { authState } = this.props;

    if (!authState?.authenticated || !authState?.expiresAt) {
      // because the /me endpoint does not return when its token expires (because it is updated on
      // use), this prevents children from rendering
      return false;
    }
    return Date.now() > this.expirationBufferStart();
  }

  componentDidMount() {
    this.determineShowState();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    let prevExpiresAt: number | undefined = undefined;
    let expiresAt: number | undefined = undefined;
    if (prevProps.authState?.authenticated) {
      prevExpiresAt = prevProps.authState.expiresAt;
    }
    if (this.props.authState?.authenticated) {
      expiresAt = this.props.authState.expiresAt;
    }
    if (prevExpiresAt !== expiresAt) {
      this.determineShowState();
    }
  }

  render() {
    const { children } = this.props;
    const { showChildren } = this.state;

    if (!showChildren) {
      return null;
    }

    return <>{children}</>;
  }
}

export default withAuthState(AuthExpiration);
